// ---------------------------------------------------------------
// Heuristic rate-confirmation parser.
// Feed it the text of a rate con; it returns fields to pre-fill a load.
// It errs toward capturing something for review, not perfect accuracy.
// ---------------------------------------------------------------

const PICKUP_WORDS = ['pick up', 'pickup', 'pick-up', 'shipper', 'origin', 'ship from', 'collection'];
const DELIV_WORDS = ['delivery', 'deliver', 'consignee', 'receiver', 'destination', 'ship to', 'drop'];

// city, ST 12345  (with their position in the text)
function locations(text) {
  return [...text.matchAll(/([A-Za-z][A-Za-z .'\-]{1,30}),\s*([A-Z]{2})\s+(\d{5})/g)].map((m) => ({
    index: m.index, city: m[1].trim(), state: m[2], zip: m[3],
  }));
}

function earliestKeyword(lowerText, words) {
  let best = -1;
  words.forEach((w) => {
    const i = lowerText.indexOf(w);
    if (i >= 0 && (best === -1 || i < best)) best = i;
  });
  return best;
}

function nearestLocAfter(idx, locs) {
  if (idx < 0) return null;
  return locs.find((l) => l.index > idx) || null;
}

function firstDateAfter(text, idx) {
  if (idx < 0) return '';
  const slice = text.slice(idx, idx + 400);
  // 12/31/2026 or 12-31-26 or Dec 31, 2026
  const m = slice.match(/(\d{1,2}[/-]\d{1,2}[/-]\d{2,4})|([A-Z][a-z]{2,8}\.?\s+\d{1,2},?\s+\d{4})/);
  if (!m) return '';
  const d = new Date(m[0].replace(/-/g, '/'));
  if (isNaN(d)) return '';
  // to datetime-local format
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T08:00`;
}

// grab a company name: the non-empty line just before a city/state/zip line
function companyNear(text, loc) {
  if (!loc) return '';
  const before = text.slice(Math.max(0, loc.index - 200), loc.index);
  const lines = before.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  // last line before the city is often the street; the one before that the company
  for (let i = lines.length - 1; i >= 0 && i >= lines.length - 3; i--) {
    const l = lines[i];
    if (l && !/\d{5}/.test(l) && !/^\d+\s/.test(l) && l.length > 2 && !/(pick|deliver|shipper|consignee|origin|destination)/i.test(l)) {
      return l.replace(/[:•\-]+$/, '').trim();
    }
  }
  return '';
}

function streetNear(text, loc) {
  if (!loc) return '';
  const before = text.slice(Math.max(0, loc.index - 120), loc.index);
  const lines = before.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const street = lines.reverse().find((l) => /^\d+\s+\S/.test(l));
  return street || '';
}

export function parseRateCon(text) {
  const lower = text.toLowerCase();
  const locs = locations(text);

  const puIdx = earliestKeyword(lower, PICKUP_WORDS);
  const delIdx = earliestKeyword(lower, DELIV_WORDS);
  const puLoc = nearestLocAfter(puIdx, locs) || locs[0] || null;
  const delLoc = nearestLocAfter(delIdx, locs) || locs[1] || locs[0] || null;

  // rate: prefer amount near "total"/"rate", else largest $
  const amts = [...text.matchAll(/\$\s?([\d,]+(?:\.\d{2})?)/g)].map((m) => ({ v: parseFloat(m[1].replace(/,/g, '')), index: m.index }));
  let rate = null;
  const rateKw = text.toLowerCase().search(/total\s*(rate|charge|amount|pay)|line\s*haul|agreed\s*rate|rate\s*confirmation|carrier\s*pay/);
  if (rateKw >= 0) {
    const near = amts.filter((a) => a.index > rateKw && a.index < rateKw + 200).sort((a, b) => b.v - a.v)[0];
    if (near) rate = near.v;
  }
  if (rate == null && amts.length) rate = Math.max(...amts.map((a) => a.v));

  const weight = (text.match(/([\d,]{3,})\s*(?:lbs?\b|pounds)/i) || [])[1];
  const pieces = (text.match(/(\d{1,4})\s*(?:pallets?|pieces?|pcs|units?|skids?|cases?)/i) || [])[1];
  const commodity = (text.match(/commodity\s*[:\-]?\s*([A-Za-z0-9 ,.&/]{3,40})/i) || [])[1];
  const weightNum = weight ? weight.replace(/,/g, '') : '';

  // temperature / reefer set point
  let temp = '';
  const tempM = text.match(/(?:temp(?:erature)?|set\s*point|reefer\s*temp|continuous)\s*[:\-]?\s*(-?\d{1,3})\s*°?\s*F?\b/i);
  const degM = text.match(/(-?\d{1,3})\s*°\s*F\b/);
  if (tempM) temp = `${tempM[1]}°F`;
  else if (degM) temp = `${degM[1]}°F`;
  else if (/\breefer|refrigerat|frozen|keep\s*frozen|protect\s*from\s*freez/i.test(text)) temp = 'REEFER';
  else if (/\bdry\s*van|\bdry\b/i.test(text)) temp = 'DRY';

  // broker's load / order / confirmation number
  const bl = text.match(/(?:load|order|confirmation|shipment)\s*(?:#|number|no\.?|id)?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-]{4,})/i);
  const brokerLoadNumber = bl ? bl[1] : '';

  // broker name: first meaningful line of the rate con header
  let brokerName = '';
  const headLines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (const l of headLines.slice(0, 8)) {
    if (l.length > 3 && !/\d{5}/.test(l) && !/^\d/.test(l) && !/(rate|confirmation|carrier|load|order|date|page)/i.test(l)) { brokerName = l.replace(/[:•\-]+$/, '').trim(); break; }
  }

  // reference numbers
  const references = [];
  const refPatterns = [
    [/(?:load|order)\s*#?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-]{3,})/i, 'Ref'],
    [/(?:pickup|pu)\s*#?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-]{3,})/i, 'Pickup#'],
    [/(?:p\.?o\.?|purchase order)\s*#?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-]{3,})/i, 'PO'],
    [/(?:bol|b\/l)\s*#?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-]{3,})/i, 'BOL'],
    [/pro\s*#?\s*[:\-]?\s*([A-Z0-9][A-Z0-9\-]{3,})/i, 'PRO'],
  ];
  refPatterns.forEach(([re, type]) => {
    const m = text.match(re);
    if (m && m[1] && !references.some((r) => r.number === m[1])) references.push({ type, number: m[1] });
  });

  const mkParty = (loc) => ({
    name: companyNear(text, loc), account: '',
    address: streetNear(text, loc),
    city: loc ? loc.city : '', state: loc ? loc.state : '', zip: loc ? loc.zip : '',
    phone: '', contact: '',
  });

  return {
    shipper: mkParty(puLoc),
    consignee: mkParty(delLoc),
    pickup: { apptReq: true, early: firstDateAfter(text, puIdx), late: '', zone: puLoc ? puLoc.zip : '' },
    delivery: { apptReq: true, early: firstDateAfter(text, delIdx), late: '', zone: delLoc ? delLoc.zip : '' },
    weight: weightNum,
    pieces: pieces || '',
    commodity: commodity ? commodity.trim() : '',
    temp,
    brokerName,
    brokerLoadNumber,
    references,
    charges: rate ? [{ code: 'LINEHAUL', description: 'Line haul (from rate con)', qty: 1, rate }] : [],
    _rate: rate,
  };
}

// Extract text from a PDF using pdf.js loaded from a CDN (no npm install needed)
export async function pdfToText(file) {
  if (!window.pdfjsLib) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      s.onload = resolve;
      s.onerror = () => reject(new Error('Could not load the PDF reader.'));
      document.head.appendChild(s);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
  }
  const buf = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;
  let out = '';
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    // keep rough line structure by y-position
    let lastY = null;
    content.items.forEach((it) => {
      const y = it.transform[5];
      if (lastY != null && Math.abs(y - lastY) > 3) out += '\n';
      out += it.str + ' ';
      lastY = y;
    });
    out += '\n';
  }
  return out;
}
